import {
    drawBlackBorderToRoofEdges, drawRoofForGivenFace, drawWallForGivenEdge, GetHouseBound, GetIdStr,
} from "./Loader";
import {m2ft} from "../../../../../Constants";
import {
    AddIndividualMode,
    addModulesToRoofAutoFill, DeleteModule,
    resetModuleInstances,
    SwitchModuleIrrMat,
    updateModuleCount
} from "./ModuleControl";
import {
    ACCESS_MULTIPLIERS, FACE_IDEAL_API, MODULE_GRAPH,
    SHADE_CALCULATIONS,
    siteCalculationState
} from "../../../../../services/siteCalculationState";
import {useViewerStore} from "../../../../../store/store";
import * as THREE from "three";
import {getModuleMeshInfo, GetRadVal, getRoofModuleMeshes} from "./Common";
import {disposeObject} from "@/components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/MeshControl";
import {SetPathwayValue} from "@/components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/PathwayControl";
import { EditAction } from "../../../../../@types/enums";

export function CreatePlaneRoof(self, position, props) {
    const dis = props?.scl ?? {x: 6.096, y: 6.096}; // Default scale in meters
    const tilt = props?.oriAng.tilt ?? 0; // Pitch angle
    const azimuth = props?.oriAng.azimuth ?? 0; // Azimuth angle
    const {roofs} = self;
    const {edges, faces} = roofs[0];
    const roofFaceId = props?.selRoofId ?? GetIdStr();
    console.log("Generated Plane Roof with ID:", roofFaceId);
    const idStr = faces.length.toString();

    let newEdges = []

    const pos3DArr = [];
    const lines = [];

    // Always apply rotations
    // Convert angles to radians
    const rotX = GetRadVal(180 - tilt);
    const rotY = GetRadVal(azimuth);

    // Define half dimensions
    const halfWidth = dis.x / 2;
    const halfDepth = dis.y / 2;

    // Define the corners of the roof in local space
    const localCorners = [
        {x: -halfWidth, y: 0, z: -halfDepth},
        {x: -halfWidth, y: 0, z: halfDepth},
        {x: halfWidth, y: 0, z: halfDepth},
        {x: halfWidth, y: 0, z: -halfDepth},
    ];

    // Apply tilt rotation (around the X-axis)
    const cosX = Math.cos(rotX);
    const sinX = Math.sin(rotX);
    const tiltedCorners = localCorners.map(corner => {
        const y = corner.y * cosX - corner.z * sinX;
        const z = corner.y * sinX + corner.z * cosX;
        return {x: corner.x, y: y, z: z};
    });

    // Apply azimuth rotation (around the Y-axis)
    const cosY = Math.cos(rotY);
    const sinY = Math.sin(rotY);
    const rotatedCorners = tiltedCorners.map(corner => {
        const x = corner.x * cosY - corner.z * sinY;
        const z = corner.x * sinY + corner.z * cosY;
        return {x: x, y: corner.y, z: z};
    });

    // Final translation and position mapping
    rotatedCorners.forEach(corner => {
        pos3DArr.push({
            x: corner.x + position.x,
            y: corner.y + (props?.height ?? 3.048),
            z: corner.z + position.z,
            oldY: corner.y + (props?.height ?? 3.048),
            zeroY: corner.y,
        });
    });




    const getBuildingNumber = ()=>{
        let buildingNumber = 1;
        if (faces.length > 0){
            const buildingNumbers = faces.map(face => face.buildingName);
            buildingNumber = Math.max(...buildingNumbers) + 1;
        }
        return buildingNumber
    }

    const getRoofTagId = () => {
        let roofTagId = 1;
        if (faces.length > 0){
            const roofTagIds = self.roofMeshArr.map(rm => rm.userData.roof_tag_id);
            roofTagId = Math.max(...roofTagIds) + 1;
        }
        return roofTagId;
    }

    const buildingNumber = props?.buildingName ?? getBuildingNumber();


    // Create new roof face
    const newFace = {
        roofId : idStr,
        roofFaceId,
        surfaceMount: true,
        posArr: pos3DArr,
        pitch: tilt,
        azimuth: azimuth,
        irrDir: {tilt: -tilt, azimuth: -azimuth},
        lines,
        size: dis.x * dis.y  * m2ft * m2ft,
        oriAng: {azimuth, tilt},
        flatRoof: true,
        plane: true,
        scl: {x: dis.x, y: dis.y},
        pos: {x: position.x, y: position.z},
        height: props?.height ?? 3.048,
        carport: props?.carport ?? false,
        buildingName : buildingNumber,
        roof_tag_id : getRoofTagId(),
        moduleArr: props?.moduleArr ?? [],
    };

    faces.push(newFace);


    // Generate edges and lines
    pos3DArr.forEach((pos3D, idx) => {
        const nextPos = idx === pos3DArr.length - 1 ? pos3DArr[0] : pos3DArr[idx + 1];
        const points = [pos3D, nextPos];
        const lineId = GetIdStr();
        const newEdge  = {posArr: points, type: 'rake', flat: true, roofFaceId, carport : props?.carport ?? false, roof_tag_id: newFace.roof_tag_id};
        edges.push(newEdge);
        newEdges.push(newEdge)
        lines.push({points, lineId, pass: true, possiblePathway: false, type: 'RAKE', flatRoof: true});
    });

    const roofGroup = new THREE.Group()

    roofGroup.houseId = GetIdStr();



    //ROOF DRAWING
    drawRoofForGivenFace({
        face: newFace,
        associatedRoofGroup: roofGroup,
        isDrawingGroundMount : true,
        meshArr : self.meshArr,
        roofMeshArr : self.roofMeshArr,
        fIdx: 3,
    });

    //WALL DRAWING
    newEdges.forEach(edge => {
        drawWallForGivenEdge( edge, roofGroup, roofGroup.houseId, self.meshArr);
    })

    const boundMesh = GetHouseBound(roofGroup);

    //DEBUG => Visualize Bound Mesh ( This shows Bound of all buildings together )
    // const bbox = new THREE.BoxHelper( boundMesh, 0xff0000 );
    // self.totalGroup.add( bbox );

    roofGroup.scale.y = roofGroup.sclY;
    roofGroup.rotation.y = self.roofRotY || 0;
    roofGroup.userData.originalPosition = roofGroup.position;


    self.roofGroup.add(roofGroup);

    self.boundGroup.add(boundMesh);

    //EDGE DRAWING ( BLACK BORDER )
    self.roofMeshArr.forEach((roofMesh) => {
        drawBlackBorderToRoofEdges( self, roofGroup, roofMesh);
    })

    const roofMesh = self.roofMeshArr.find(mesh => mesh.roofFaceId === roofFaceId);
    roofMesh.moduleArr = newFace.moduleArr

    if(newFace.moduleArr.length > 0){

        //Add Modules to Roof
        newFace.moduleArr.forEach((item) => {
            AddIndividualMode(roofMesh, item, true, self.moduleMeshArr, self.meshArr, false,true);
        });

        const roofModules = [];

        self.roofMeshArr.forEach(roof => {
            roofModules.push({
                roofFaceId: roof.roofFaceId,
                moduleArr: getRoofModuleMeshes(roof).map(m => getModuleMeshInfo(m))
            });

        });



        useViewerStore.setState({savedRoofModulesLayout: roofModules});

    }
    else {
        //ADD MODULES
        addModulesToRoofAutoFill(self,roofMesh,null,"land", true);
    }


    // //ADD MODULES
    // addModulesToRoofAutoFill(self,roofMesh,null,"land", true);

    siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS);
    siteCalculationState.useCalculations(ACCESS_MULTIPLIERS);
    siteCalculationState.needsUpdate(FACE_IDEAL_API);
    siteCalculationState.useCalculations(FACE_IDEAL_API);
    self.refreshShade()

    // Update site calculations and switch modules


    // siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
    // siteCalculationState.needsUpdate(MODULE_GRAPH);
    // siteCalculationState.useCalculations(MODULE_GRAPH);
    SwitchModuleIrrMat(self, true);
    self.setCreatePlaneMode(false);
    self.ifContainsFlatRoofAndEnableBallastedRacking();
    updateModuleCount(self,"land",false,roofFaceId);


    // Select the newly created flat roof
    setTimeout(() => {
        SelectFlatRoof(self, roofFaceId);
    }, 100);
}

export function UpdatePlaneRoof(self, property, value, isUndo) {

    //Fetch selected roof Mesh
    const {selFlatRoof} = self;
    if (!selFlatRoof) return;

    //Fetch selected roof info
    const selRoofId = selFlatRoof.roofFaceId;
    const faceIndex = self.roofs[0].faces.findIndex(face => face.roofFaceId === selRoofId);
    if (faceIndex === -1) return;
    const roofInfo = self.roofs[0].faces[faceIndex];

    //deep copy of old module layout selFlatRoof.moduleArr.
    const oldModuleLayoutInfo = selFlatRoof.moduleArr ? JSON.parse(JSON.stringify(selFlatRoof.moduleArr)): [];

    //Remove the old roof
    DeletePlaneRoof(self);

    //Store new + old values for undo/redo
    const newVal = value;
    const oldValue = property === "carport" ? roofInfo.carport
        : property === "height" ? roofInfo.height
            : property === "azimuth" ? roofInfo.oriAng.azimuth
                : property === "pitch" ? roofInfo.oriAng.tilt
                    : property === "pos" ? roofInfo.pos
                        : property === "sclX" ? roofInfo.scl.x
                            : property === "sclY" ? roofInfo.scl.y : 0;

    // Update selected roof info object with the updated property
    if (property === 'pos') {
        roofInfo.pos = { x: value.x, y: value.y};
    } else if (property === 'sclX') {
        roofInfo.scl = {...roofInfo.scl, x: value};
    } else if (property === 'sclY') {
        roofInfo.scl = {...roofInfo.scl, y: value};
    } else if (property === 'pitch') {
        roofInfo.pitch = value;
        roofInfo.oriAng = {...roofInfo.oriAng, tilt: value};
        roofInfo.irrDir = {...roofInfo.irrDir, tilt: value};
    } else if (property === 'azimuth') {
        roofInfo.azimuth = value;
        roofInfo.oriAng = {...roofInfo.oriAng, azimuth: value};
        roofInfo.irrDir = {...roofInfo.irrDir, azimuth: value};
    }
    else if (property === 'height') {
        roofInfo.height = value;
    }
    else if (property === 'carport') {
        roofInfo.carport = value;
    }
    else {
        roofInfo[property] = value;
    }

    roofInfo.moduleArr = oldModuleLayoutInfo;

    const {scl, pos, height, oriAng, carport, buildingName} = roofInfo;


    CreatePlaneRoof(self, {x: pos.x, z: pos.y} , {...roofInfo, selRoofId: selRoofId, buildingName});



    if (!isUndo ){
        self.addStep(EditAction.UpdatePlaneRoof, {property, selRoofId}, oldValue, newVal);
    }
}

export function DeletePlaneRoof(self) {
    const {selFlatRoof, roofs} = self;
    if (!selFlatRoof) return;
    const selRoofId = selFlatRoof.roofFaceId;
    const  roofMesh = self.roofMeshArr.find(mesh => mesh.roofFaceId === selRoofId);
    const modules = getRoofModuleMeshes(roofMesh);
    DeleteModule(modules, roofMesh, self.moduleMeshArr, self);
    roofs[0].faces = roofs[0].faces.filter(face => face.roofFaceId !== selRoofId);
    roofs[0].edges = self.roofs[0].edges.filter(edge => edge.roofFaceId !== selRoofId);


    const edgeMeshes = self.edgeMeshArr.filter(mesh => mesh.userData.roofFaceId === selRoofId);
    const wallMeshArr = roofMesh
        ? self.wallMeshArr.filter(mesh => mesh.roofFaceId === roofMesh.roofFaceId)
        : [];



    if (roofMesh) disposeObject(roofMesh);
    edgeMeshes.forEach(mesh => disposeObject(mesh));
    wallMeshArr.forEach(mesh => disposeObject(mesh));




    // // Remove disposed items from stored arrays
    if (roofMesh) {
        self.roofMeshArr = self.roofMeshArr.filter(m => m !== roofMesh);
    }
    self.edgeMeshArr = self.edgeMeshArr.filter(m => !edgeMeshes.includes(m));
    self.wallMeshArr = self.wallMeshArr.filter(m => !wallMeshArr.includes(m));


    const removedSet = new Set([...(roofMesh ? [roofMesh] : []), ...edgeMeshes, ...wallMeshArr]);
    self.meshArr = self.meshArr.filter(m => !removedSet.has(m));

    siteCalculationState.needsUpdate(MODULE_GRAPH);
    siteCalculationState.useCalculations(MODULE_GRAPH);
    siteCalculationState.needsUpdate(SHADE_CALCULATIONS);

    SwitchModuleIrrMat(self, true);
    self.setCreatePlaneMode(false);
}

export function SetupFlatRoof(self, interObj) {
    const flatRoofArr = self.roofMeshArr.filter(item => {
        return item.flatRoof === true
    });
    flatRoofArr.forEach(item => {
        item.material.color.setHex(item.oriColor);
        item.material.map = item.oriMap;
    });
    self.selFlatRoof = undefined;
    if (!interObj) return;
    SelectFlatRoof(self, interObj.object.roofFaceId)
}

export function SelectFlatRoof(self, selRoofId) {
    self.selFlatRoof = self.roofMeshArr.find(item => item.roofFaceId === selRoofId);
    const selRoofInfo = self.roofs[0].faces.find(item => item.roofFaceId === selRoofId);

    if (!self.selFlatRoof || !selRoofInfo) {

        useViewerStore.setState({
            selectedFlatRoofAzimuth: 0,
            selectedFlatRoofPitch: 0,
            selectedFlatRoofHeight: 0,
            selectedFlatRoofPosX: 0,
            selectedFlatRoofPosY: 0,
            selectedFlatRoofScaleX: 0,
            selectedFlatRoofScaleY: 0,
        });


    }
    else {
        self.selFlatRoof.material.color.setHex(0x0000FF);
        self.selFlatRoof.material.map = undefined;
        self.selectType = 'surface';

        const {height, oriAng, pos, scl} = selRoofInfo, {azimuth, tilt} = oriAng;
        const sclXFeet = scl.x * m2ft;
        const sclYFeet = scl.y * m2ft;
        const heightFeet = height * m2ft;

        useViewerStore.setState({
            selectedFlatRoofHeight: heightFeet,
            selectedFlatRoofAzimuth: azimuth,
            selectedFlatRoofPitch: tilt,
            selectedFlatRoofPosX: pos.x,
            selectedFlatRoofPosY: pos.y,
            selectedFlatRoofScaleX: sclXFeet,
            selectedFlatRoofScaleY: sclYFeet,
            selectedFlatRoofIsCarport: selRoofInfo.carport
        });


    }
}

export function initPlaneRoofPoints(self) {
    const posSize = 0.01, virFlatIn = new THREE.Group(), virPlaneIn = new THREE.Group();
    const posGeo = new THREE.BoxGeometry(posSize, posSize, posSize),
        posMat = new THREE.MeshStandardMaterial({color: 0x333333}),
        posMesh = new THREE.Mesh(posGeo, posMat);
    [{x: 1, z: 1}, {x: 1, z: -1}, {x: -1, z: -1}, {x: -1, z: 1}].forEach((pos, idx) => {
        const virFlatPos = posMesh.clone(), virPlanePos = posMesh.clone();
        virFlatPos.position.set(pos.x * 10, 0, pos.z * 10);
        virPlanePos.position.set(pos.x / 2, 0, pos.z / 2);
        virFlatIn.add(virFlatPos);
        virPlaneIn.add(virPlanePos);
    });
    self.virFlatPoints = new THREE.Group();
    self.virFlatPoints.add(virFlatIn);
    self.virPlanePoints = new THREE.Group();
    self.virPlanePoints.add(virPlaneIn);
    self.totalGroup.add(self.virFlatPoints, self.virPlanePoints);
    self.virFlatPlane = new THREE.Plane();
}

export const isGroundMount = (roofMesh) => {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    return sceneViewer.roofs[0].faces.find(face => face?.roofFaceId === roofMesh?.roofFaceId)?.surfaceMount;
}
